import { useState } from "react";
import { REACTION_OPTIONS } from "@/villa142/config";
import { UploadTray } from "@/villa142/uploads";

const uid = () => Math.random().toString(36).slice(2, 10);
export const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const grow = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

export const AutoTextarea = ({ id, value, onChange, rows = 3, placeholder, ariaLabel, testid, small }) => (
  <textarea
    id={id}
    ref={(el) => el && grow(el)}
    data-testid={testid}
    className={`v142-textarea${small ? " v142-textarea-small" : ""}`}
    rows={rows}
    placeholder={placeholder}
    aria-label={ariaLabel}
    value={value || ""}
    onChange={(e) => {
      onChange(e.target.value);
      grow(e.target);
    }}
  />
);

const NoteField = ({ qid, value, onValue, placeholder }) => (
  <div className="v142-notefield">
    <label htmlFor={`note-${qid}`} className="v142-mini-label">A note, if you like</label>
    <AutoTextarea id={`note-${qid}`} testid={`v142-note-${qid}`} rows={2} small value={value.note} placeholder={placeholder} onChange={(t) => onValue({ ...value, note: t })} />
  </div>
);

export const ChipRow = ({ qid, options, selected, onToggle, multi, name, asRadio }) => {
  if (asRadio && !multi) {
    const onKey = (e, i) => {
      const dir = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
      if (!dir) return;
      e.preventDefault();
      const btns = [...e.currentTarget.parentElement.querySelectorAll("[role='radio']")];
      const next = btns[(i + dir + btns.length) % btns.length];
      if (next) { next.focus(); onToggle(options[(i + dir + options.length) % options.length]); }
    };
    return (
      <div className="v142-chips" role="radiogroup" aria-label={name}>
        {options.map((opt, i) => {
          const active = selected === opt;
          return (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`v142-chip-${qid}-${slug(opt)}`}
              className={`v142-chip${active ? " is-active" : ""}`}
              onClick={() => onToggle(opt)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {opt}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className="v142-chips" role="group" aria-label={name}>
      {options.map((opt) => {
        const active = multi ? (selected || []).includes(opt) : selected === opt;
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={active}
            data-testid={`v142-chip-${qid}-${slug(opt)}`}
            className={`v142-chip${active ? " is-active" : ""}`}
            onClick={() => onToggle(opt)}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};

export const TagsInput = ({ qid, tags, onChange, label = "Add colour or material tags" }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (t && !(tags || []).includes(t)) onChange([...(tags || []), t]);
    setDraft("");
  };
  return (
    <div className="v142-tags">
      {(tags || []).map((t) => (
        <span key={t} className="v142-tag">
          {t}
          <button type="button" aria-label={`Remove ${t}`} data-testid={`v142-tag-remove-${qid}-${slug(t)}`} className="v142-tag-x" onClick={() => onChange(tags.filter((x) => x !== t))}>×</button>
        </span>
      ))}
      <input
        type="text"
        className="v142-tag-input"
        data-testid={`v142-tag-input-${qid}`}
        placeholder={label}
        aria-label={label}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
        }}
        onBlur={add}
      />
    </div>
  );
};

export const TextComposer = ({ question, value, onValue }) => {
  const cfg = question.config || {};
  return (
    <div className="v142-composer-body">
      <AutoTextarea
        id={`v142-${question.id}`}
        testid={`v142-input-${question.id}`}
        rows={question.input === "short_text" ? 2 : 4}
        ariaLabel={question.label}
        value={value.text}
        onChange={(t) => onValue({ ...value, text: t })}
      />
      {cfg.chips && (
        <div className="v142-chips-aside">
          <span className="v142-mini-label">{cfg.chipsLabel || "Feelings that fit, alongside your words"}</span>
          <ChipRow
            qid={question.id}
            options={cfg.chips}
            selected={value.chips || []}
            multi
            name={cfg.chipsLabel || "Feelings"}
            onToggle={(opt) => {
              const cur = value.chips || [];
              onValue({ ...value, chips: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] });
            }}
          />
          {cfg.chipsOther && (value.chips || []).includes("Other") && (
            <div className="v142-mini-field">
              <label htmlFor={`other-${question.id}`} className="v142-mini-label">A word about “Other”, if you like</label>
              <input
                id={`other-${question.id}`}
                data-testid={`v142-other-${question.id}`}
                className="v142-input v142-input-small"
                type="text"
                value={value.other || ""}
                onChange={(e) => onValue({ ...value, other: e.target.value })}
              />
            </div>
          )}
        </div>
      )}
      {cfg.tags && <TagsInput qid={question.id} tags={value.tags} onChange={(tags) => onValue({ ...value, tags })} />}
    </div>
  );
};

const DetailFields = ({ question, fields, value, onValue }) => (
  <div className="v142-detail-fields">
    {fields.map((f) => (
      <div key={f.key} className="v142-mini-field">
        <label htmlFor={`df-${question.id}-${f.key}`} className="v142-mini-label">{f.label}</label>
        <input
          id={`df-${question.id}-${f.key}`}
          data-testid={`v142-field-${question.id}-${f.key}`}
          className="v142-input v142-input-small"
          type="text"
          value={value.details?.fields?.[f.key] || ""}
          onChange={(e) =>
            onValue({ ...value, details: { ...(value.details || {}), fields: { ...(value.details?.fields || {}), [f.key]: e.target.value } } })
          }
        />
      </div>
    ))}
  </div>
);

const DetailItems = ({ question, itemLabel, value, onValue }) => {
  const items = value.details?.items || [];
  const setItems = (items2) => onValue({ ...value, details: { ...(value.details || {}), items: items2 } });
  return (
    <div className="v142-items">
      {items.map((it, i) => (
        <div key={it.id} className="v142-item-row">
          <input
            className="v142-input v142-input-small"
            type="text"
            aria-label={itemLabel}
            placeholder={itemLabel}
            data-testid={`v142-detail-item-${question.id}-${i}`}
            value={it.label || ""}
            onChange={(e) => setItems(items.map((x) => (x.id === it.id ? { ...x, label: e.target.value } : x)))}
          />
          <button type="button" className="v142-link" aria-label={`Remove ${itemLabel}`} onClick={() => setItems(items.filter((x) => x.id !== it.id))}>Remove</button>
        </div>
      ))}
      <button type="button" className="v142-add-row" data-testid={`v142-add-detail-item-${question.id}`} onClick={() => setItems([...items, { id: uid(), label: "" }])}>
        + Add {itemLabel.toLowerCase()}
      </button>
    </div>
  );
};

const DetailCards = ({ question, details, value, onValue }) => {
  const items = value.details?.items || [];
  const setItems = (items2) => onValue({ ...value, details: { ...(value.details || {}), items: items2 } });
  const label = details.itemLabel || "Consideration";
  return (
    <div className="v142-items">
      {details.intro && <p className="v142-support">{details.intro}</p>}
      {items.map((it, i) => (
        <div key={it.id} className="v142-person-card" data-testid={`v142-card-${question.id}-${i}`}>
          {(details.fields || []).map((f) => (
            <div key={f.key} className="v142-mini-field">
              <label htmlFor={`c-${question.id}-${it.id}-${f.key}`} className="v142-mini-label">{f.label}</label>
              <input
                id={`c-${question.id}-${it.id}-${f.key}`}
                data-testid={`v142-card-${question.id}-${i}-${f.key}`}
                className="v142-input v142-input-small"
                type="text"
                value={it[f.key] || ""}
                onChange={(e) => setItems(items.map((x) => (x.id === it.id ? { ...x, [f.key]: e.target.value } : x)))}
              />
            </div>
          ))}
          <button type="button" className="v142-link v142-person-remove" onClick={() => setItems(items.filter((x) => x.id !== it.id))}>Remove</button>
        </div>
      ))}
      <button type="button" className="v142-add-row" data-testid={`v142-add-card-${question.id}`} onClick={() => setItems([...items, { id: uid() }])}>
        + Add {label.toLowerCase()}
      </button>
    </div>
  );
};

export const ChoiceComposer = ({ question, value, onValue }) => {
  const cfg = question.config || {};
  const details = cfg.details;
  const showDetails = details && (details.when || []).includes(value.choice);
  return (
    <div className="v142-composer-body">
      <ChipRow qid={question.id} options={cfg.options || []} selected={value.choice} name={question.label} asRadio={cfg.radio} onToggle={(opt) => onValue({ ...value, choice: value.choice === opt ? "" : opt })} />
      {cfg.extraField && (
        <div className="v142-mini-field">
          <label htmlFor={`ef-${question.id}`} className="v142-mini-label">{cfg.extraField.label}</label>
          <input
            id={`ef-${question.id}`}
            data-testid={`v142-extra-${question.id}`}
            className="v142-input v142-input-small"
            type="text"
            value={value[cfg.extraField.key] || ""}
            onChange={(e) => onValue({ ...value, [cfg.extraField.key]: e.target.value })}
          />
        </div>
      )}
      {showDetails && details.input === "long_text" && (
        <AutoTextarea
          id={`dt-${question.id}`}
          testid={`v142-details-${question.id}`}
          rows={3}
          ariaLabel="Tell us more"
          placeholder="You may share as much or as little as you like."
          value={value.details?.text}
          onChange={(t) => onValue({ ...value, details: { ...(value.details || {}), text: t } })}
        />
      )}
      {showDetails && details.input === "items_list" && (
        <DetailItems question={question} itemLabel={details.itemLabel || "Item"} value={value} onValue={onValue} />
      )}
      {showDetails && details.input === "fields" && (
        <DetailFields question={question} fields={details.fields || []} value={value} onValue={onValue} />
      )}
      {showDetails && details.input === "cards" && (
        <DetailCards question={question} details={details} value={value} onValue={onValue} />
      )}
      {cfg.reflection && (
        <div className="v142-notefield">
          <label htmlFor={`refl-${question.id}`} className="v142-mini-label">{cfg.reflectionLabel || "Your reflection, if you like"}</label>
          <AutoTextarea
            id={`refl-${question.id}`}
            testid={`v142-input-${question.id}`}
            rows={3}
            ariaLabel={cfg.reflectionLabel || "Your reflection"}
            value={value.text}
            onChange={(t) => onValue({ ...value, text: t })}
          />
        </div>
      )}
      {cfg.note && <NoteField qid={question.id} value={value} onValue={onValue} placeholder={cfg.notePlaceholder} />}
    </div>
  );
};

export const MultiChoiceComposer = ({ question, value, onValue }) => {
  const cfg = question.config || {};
  return (
    <div className="v142-composer-body">
      <ChipRow
        qid={question.id}
        options={cfg.options || []}
        selected={value.choices || []}
        multi
        name={question.label}
        onToggle={(opt) => {
          const cur = value.choices || [];
          onValue({ ...value, choices: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] });
        }}
      />
      {cfg.note && <NoteField qid={question.id} value={value} onValue={onValue} placeholder={cfg.notePlaceholder} />}
    </div>
  );
};

export const ScaleComposer = ({ question, value, onValue }) => {
  const cfg = question.config || {};
  const labeled = Array.isArray(cfg.options) && cfg.options.length > 0;
  const setValue = (n) => onValue({ ...value, value: value.value === n ? undefined : n });
  return (
    <div className="v142-composer-body">
      {labeled ? (
        <div className="v142-continuum">
          <div className="v142-continuum-ends" aria-hidden="true">
            <span className="v142-continuum-end">{cfg.left}</span>
            <span className="v142-continuum-end">{cfg.right}</span>
          </div>
          <div className="v142-continuum-opts" role="radiogroup" aria-label={question.label}>
            {cfg.options.map((opt, i) => {
              const n = i + 1;
              const active = value.value === n;
              return (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={`${opt} — ${cfg.left} to ${cfg.right}`}
                  data-testid={`v142-scale-${question.id}-${n}`}
                  className={`v142-continuum-opt${active ? " is-active" : ""}`}
                  onClick={() => setValue(n)}
                  onKeyDown={(e) => {
                    const dir = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
                    if (!dir) return;
                    e.preventDefault();
                    const btns = [...e.currentTarget.parentElement.querySelectorAll("[role='radio']")];
                    const nb = btns[(i + dir + btns.length) % btns.length];
                    if (nb) { nb.focus(); onValue({ ...value, value: ((i + dir + btns.length) % btns.length) + 1 }); }
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="v142-scale" role="group" aria-label={question.label}>
          <span className="v142-scale-label">{cfg.left}</span>
          <div className="v142-scale-track">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={value.value === n}
                aria-label={`${n} of 5, between ${cfg.left} and ${cfg.right}`}
                data-testid={`v142-scale-${question.id}-${n}`}
                className={`v142-scale-dot${value.value === n ? " is-active" : ""}`}
                onClick={() => setValue(n)}
              />
            ))}
          </div>
          <span className="v142-scale-label">{cfg.right}</span>
        </div>
      )}
      {cfg.note && <NoteField qid={question.id} value={value} onValue={onValue} placeholder={cfg.notePlaceholder} />}
    </div>
  );
};

export const PeopleListComposer = ({ question, value, onValue, addLabel }) => {
  const cfg = question.config || {};
  const items = value.items || [];
  const setItems = (items2) => onValue({ ...value, items: items2 });
  const addText = addLabel || (cfg.itemLabel || "person").toLowerCase();
  return (
    <div className="v142-composer-body">
      <div className="v142-items">
        {items.map((it, i) => (
          <div key={it.id} className="v142-person-card" data-testid={`v142-person-${question.id}-${i}`}>
            {(cfg.fields || []).map((f) => (
              <div key={f.key} className="v142-mini-field">
                <label htmlFor={`p-${question.id}-${it.id}-${f.key}`} className="v142-mini-label">{f.label}</label>
                <input
                  id={`p-${question.id}-${it.id}-${f.key}`}
                  className="v142-input v142-input-small"
                  type="text"
                  value={it[f.key] || ""}
                  onChange={(e) => setItems(items.map((x) => (x.id === it.id ? { ...x, [f.key]: e.target.value } : x)))}
                />
              </div>
            ))}
            <button type="button" className="v142-link v142-person-remove" onClick={() => setItems(items.filter((x) => x.id !== it.id))}>Remove</button>
          </div>
        ))}
      </div>
      <button type="button" className="v142-add-row" data-testid={`v142-add-person-${question.id}`} onClick={() => setItems([...items, { id: uid() }])}>
        + Add {addText}
      </button>
      {cfg.note && <NoteField qid={question.id} value={value} onValue={onValue} placeholder={cfg.notePlaceholder} />}
    </div>
  );
};

export const ItemsListComposer = ({ question, value, onValue, contributor, media, ops }) => {
  const cfg = question.config || {};
  const items = value.items || [];
  const setItems = (items2) => onValue({ ...value, items: items2 });
  return (
    <div className="v142-composer-body">
      <div className="v142-items">
        {items.map((it, i) => (
          <div key={it.id} className="v142-object-card" data-testid={`v142-item-${question.id}-${i}`}>
            <div className="v142-item-row">
              <input
                className="v142-input v142-input-small"
                type="text"
                aria-label={cfg.itemLabel || "Item"}
                placeholder={cfg.itemLabel || "Item"}
                value={it.label || ""}
                onChange={(e) => setItems(items.map((x) => (x.id === it.id ? { ...x, label: e.target.value } : x)))}
              />
              <button type="button" className="v142-link" onClick={() => setItems(items.filter((x) => x.id !== it.id))}>Remove</button>
            </div>
            {cfg.itemUploads && (
              <UploadTray
                cfg={{ key: question.id, max: 2, label: null, captionPrompt: "A few words about it?" }}
                contributor={contributor}
                media={media}
                ops={ops}
                itemId={it.id}
                compact
              />
            )}
          </div>
        ))}
      </div>
      <button type="button" className="v142-add-row" data-testid={`v142-add-item-${question.id}`} onClick={() => setItems([...items, { id: uid(), label: "" }])}>
        + Add {(cfg.itemLabel || "item").toLowerCase()}
      </button>
      {cfg.note && <NoteField qid={question.id} value={value} onValue={onValue} placeholder={cfg.notePlaceholder} />}
    </div>
  );
};

export const VisualStudyComposer = ({ question, value, onValue }) => (
  <div className="v142-composer-body">
    <span className="v142-mini-label">Your first reaction</span>
    <ChipRow qid={question.id} options={REACTION_OPTIONS} selected={value.reaction} name="Your first reaction" onToggle={(opt) => onValue({ ...value, reaction: value.reaction === opt ? "" : opt })} />
    <label htmlFor={`vs-${question.id}`} className="v142-label-follow" style={{ marginTop: "0.6rem" }}>What draws you in, or pushes you away?</label>
    <AutoTextarea id={`vs-${question.id}`} testid={`v142-input-${question.id}`} rows={3} ariaLabel="What draws you in, or pushes you away?" value={value.text} onChange={(t) => onValue({ ...value, text: t })} />
  </div>
);

export const COMPOSERS = {
  long_text: TextComposer,
  short_text: TextComposer,
  choice: ChoiceComposer,
  multi_choice: MultiChoiceComposer,
  scale: ScaleComposer,
  people_list: PeopleListComposer,
  items_list: ItemsListComposer,
  visual_study: VisualStudyComposer,
};
